import { z } from 'zod';
import { Role } from '../enums.js';
import { IsoDateTime, UuidString } from '../primitives.js';

/** AuditEvent — append-only запись write-действия через gateway или прямой write. */
export const AuditEvent = z.object({
  id: UuidString,
  entity_id: UuidString,
  occurred_at: IsoDateTime,
  actor_user_id: UuidString.nullable(),
  actor_role: Role.nullable(),
  /**
   * B5 (service-role) — `service` рядом с человеком и агентом: чтения интеграции
   * отличимы в журнале. `human` — историческое значение из gateway-слоя, оно жило
   * и до пака; схема принимает оба, потому что прошлые записи не переписываются.
   */
  actor_kind: z.enum(['user', 'human', 'agent', 'system', 'adapter', 'service']),
  action: z.string().min(1), // e.g. "stock_entry.submit"
  resource_type: z.string().min(1),
  resource_id: z.string().nullable(),
  tier: z.number().int().min(0).max(3),
  tool_name: z.string().nullable(),
  request_payload: z.record(z.unknown()).nullable(),
  response_summary: z.record(z.unknown()).nullable(),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  status: z.enum(['ok', 'denied', 'failed']),
  error_code: z.string().nullable(),
  approvals: z
    .array(
      z.object({
        user_id: UuidString,
        role: Role,
        signed_at: IsoDateTime,
        reason: z.string().optional(),
      }),
    )
    .default([]),
  /** UI-алиас для `{actor_kind, actor_user_id, actor_role}`. */
  actor: z
    .object({
      user_id: UuidString.nullable(),
      role: Role.nullable(),
      kind: z.enum(['user', 'agent', 'system', 'adapter']),
    })
    .optional(),
  /** UI-алиас для `request_payload`. */
  payload: z.record(z.unknown()).nullable().optional(),
  /** Latency между приёмом и записью в ms — может отсутствовать в старых событиях. */
  latency_ms: z.number().int().nonnegative().optional(),
  /** Trace id для correlation с OTel. */
  trace_id: z.string().optional(),
});
export type AuditEvent = z.infer<typeof AuditEvent>;

/**
 * B81 пака `tails-closeout` (WO-B1, SOLUTION § 10.6 п. 8) — ответ журнала сущности
 * `GET /audit/:entity_type/:entity_id`. Своя схема, а не `AuditEvent`: ручка отдаёт выжимку для
 * панели детальной страницы (без тела запроса), и разбор полной схемой падал на отсутствующих
 * полях. `around` — окно вокруг записи (`?around=<id>`): `found = false` — записи нет в области
 * видимости, выдача — последние записи.
 */
export const EntityAuditItem = z.object({
  id: UuidString,
  occurred_at: IsoDateTime,
  actor_user_id: UuidString.nullable(),
  actor_role: z.string().nullable(),
  actor_kind: z.string(),
  action: z.string(),
  tool_name: z.string().nullable(),
  tier: z.number().int().min(0).max(3),
  status: z.string(),
  approvals: z.array(
    z.object({
      user_id: UuidString,
      role: z.string(),
      signed_at: IsoDateTime,
    }),
  ),
});
export type EntityAuditItem = z.infer<typeof EntityAuditItem>;

export const EntityAuditResponse = z.object({
  items: z.array(EntityAuditItem),
  /**
   * С-I2 Р.4 (WO-B3): человек вне компаний смотрящего — без имени и почты, `hidden` называет его
   * («администратор платформы» / «сотрудник вне ваших компаний»); почта — только администратору.
   * С-I3 Р.4: виден и по активному членству в холдинге компании смотрящего; отключённый сотрудник
   * этой же компании — с именем и `disabled: 'membership'`; отключённый целиком аккаунт —
   * `disabled: 'account'` (пометка «отключён», причина своя).
   */
  users: z.record(
    z.object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      hidden: z.enum(['platform_admin', 'other_company']).optional(),
      disabled: z.enum(['membership', 'account']).optional(),
    }),
  ),
  around: z.object({ id: UuidString, found: z.boolean() }).optional(),
  /**
   * F105 пака `tails-closeout` (С-I): всего записей у сущности и есть ли записи за краями выдачи —
   * экран говорит «показаны N из M» и подгружает `before`/`after`.
   */
  total: z.number().int().nonnegative().optional(),
  has_newer: z.boolean().optional(),
  has_older: z.boolean().optional(),
});
export type EntityAuditResponse = z.infer<typeof EntityAuditResponse>;
